import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MaterialTable from "material-table";
import { useTranslation } from "react-i18next";
import { makeStyles, ThemeProvider } from "@mui/styles";
import { colors } from "../components/Theme/WebTheme";
import { bookingHistoryColumns, SECONDORY_COLOR } from "../common/sharedFunctions";
import theme from "../styles/tableStyle";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { api } from "common";
import AlertDialog from "../components/AlertDialog";
import CircularLoading from "../components/CircularLoading";

const useStyles = makeStyles(() => ({
  root: {
    padding: 24,
    background: colors.WHITE,
    minHeight: '100vh',
    boxSizing: 'border-box',
    '@media (max-width: 900px)': {
      padding: 12,
    },
    '@media (max-width: 600px)': {
      padding: 4,
    },
  },
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    boxSizing: 'border-box',
    '@media (max-width: 600px)': {
      minWidth: 320,
    },
  },
}));

const AssignedRide = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const role = useSelector((state) => state.auth.profile.usertype);
  const settings = useSelector((state) => state.settingsdata.settings);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const dispatch = useDispatch();
  const { updateBooking, fetchBookings } = api;

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [putoutReason, setPutoutReason] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  useEffect(() => {
    if (bookinglistdata.bookings) {
      setData(bookinglistdata.bookings.filter(booking => booking.status === "ACCEPTED"));
    } else {
      setData([]);
    }
  }, [bookinglistdata.bookings]);

  const columns = bookingHistoryColumns(role, settings, t, isRTL).map(col => {
    if (col.field === 'status' || col.field === 'booking_status_web') {
      return {
        ...col,
        cellStyle: rowData => ({
          backgroundColor: rowData.status === 'CANCELLED' ? colors.RED : undefined,
          color: rowData.status === 'CANCELLED' ? colors.WHITE : undefined,
          fontWeight: rowData.status === 'CANCELLED' ? 'bold' : undefined,
          borderRadius: rowData.status === 'CANCELLED' ? '10px' : undefined,
          textAlign: 'center',
          padding: 3,
        })
      };
    }
    return col;
  });

  // Add updateBookingStatus function
  const updateBookingStatus = () => {
    if (selectedBooking) {
      setLoading(true);
      const updatedBooking = { 
        ...selectedBooking, 
        status: "NEW",
        putoutdrivers: [
          {
            driver: "",
            driverDeviceId: "",
            driverRating: "",
            driver_contact: "",
            driver_image: "",
            driver_name: "",
            driver_share: "",
            driver_token: "",
            putout_res: putoutReason
          }
        ]
      };
      console.log('Updated Booking:', updatedBooking);
      dispatch(updateBooking(updatedBooking));
      dispatch(fetchBookings());
      setLoading(false);
      setCommonAlert({ open: true, msg: t("Booking status updated successfully.") });
    }
    setModalOpen(false);
    setPutoutReason("");
    setSelectedBooking(null);
  };
   
  // Modal handlers
  const handlePutoutClick = (rowData) => {
    setSelectedBooking(rowData);
    setPutoutReason("");
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setPutoutReason("");
    setSelectedBooking(null);
  };

  const handleModalConfirm = () => {
    updateBookingStatus();
  };

  return (
    <div className={classes.root}>
      <ThemeProvider theme={theme}>
        <Box className={classes.tableContainer}>
          <MaterialTable
            title={t("ASSIGNED RIDE")}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
              borderRadius: "8px",
              boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
              minWidth: 320,
            }}
            columns={columns}
            data={data}
            onRowClick={(evt, selectedRow) => setSelectedRow(selectedRow.tableData.id)}
            actions={[
              {
                icon: () => (
                  <button
                    style={{
                      backgroundColor: colors.RED,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '5px',
                      padding: '6px 16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                    }}
                  >
                    PUTOUT
                  </button>
                ),
                tooltip: 'PUTOUT',
                onClick: (event, rowData) => {
                  handlePutoutClick(rowData);
                },
              },
            ]}
            options={{
              pageSize: 100,
              pageSizeOptions: [100, 500, { value: data.length, label: t("All") }],
              toolbarbuttonalignment: "right",
              exportButton: true,
              maxColumnSort: "all_columns",
              rowStyle: rowData => ({
                backgroundColor: selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
              }),
              maxBodyHeight: "calc(100vh - 199.27px)",
              headerStyle: {
                position: "sticky",
                top: "0px",
                backgroundColor: SECONDORY_COLOR,
                color: colors.Black,
                fontWeight: "bold ",
                textAlign: "center",
                zIndex: 1,
                border: `1px solid ${colors.TABLE_BORDER}`,
              },
              cellStyle: {
                border: `1px solid ${colors.TABLE_BORDER}`,
                textAlign: "center",
                margin: "auto",
                minWidth: 100,
                fontSize: '0.95rem',
                padding: 4,
              },
              actionsColumnIndex: -1,
            }}
            localization={{
              toolbar: {
                searchPlaceholder: t("search"),
                exportTitle: t("export"),
                exportCSVName: t("export"),
              },
              header: {
                actions: t("actions"),
              },
              pagination: {
                labelDisplayedRows: "{from}-{to} " + t("of") + " {count}",
                firstTooltip: t("first_page_tooltip"),
                previousTooltip: t("previous_page_tooltip"),
                nextTooltip: t("next_page_tooltip"),
                lastTooltip: t("last_page_tooltip"),
              },
              body: {
                emptyDataSourceMessage: t("No records to display"),
              },
            }}
          />
        </Box>
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
          <DialogTitle>{t('PUTOUT Reason')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="putout-reason"
              label={t('PUTOUT Reason')}
              type="text"
              fullWidth
              multiline
              minRows={3}
              placeholder="PUTOUT Reason"
              value={putoutReason}
              onChange={e => setPutoutReason(e.target.value)}
              variant="outlined"
              inputProps={{ style: { fontSize: fullScreen ? 16 : 18 } }}
            />
          </DialogContent>
          <DialogActions style={{ padding: fullScreen ? 8 : 16 }}>
            <Button onClick={handleModalClose} color="secondary" variant="outlined" fullWidth={fullScreen}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleModalConfirm} color="primary" variant="contained" disabled={!putoutReason.trim()} fullWidth={fullScreen}>
              {t('Confirm')}
            </Button>
          </DialogActions>
        </Dialog>
        <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
          {commonAlert.msg}
        </AlertDialog>
        {loading && <CircularLoading />}
      </ThemeProvider>
    </div>
  );
};

export default AssignedRide; 